import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, ChevronRight, Star, Home } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import QuestIllustration from '../components/common/formbuilder/QuestIllustration';
import XPPopup from '../components/common/formbuilder/XPPopup';
import { getThemeColors } from '../utils/themeConfig';
import LoadingScreen from '../components/common/LoadingScreen';

// Custom hook for XP management
const useXPManagement = () => {
  const [totalXP, setTotalXP] = useState(0);
  const [questXP, setQuestXP] = useState(0);
  const [showXPPopup, setShowXPPopup] = useState(false);
  const [currentQuestionXP, setCurrentQuestionXP] = useState(0);

  const addXP = useCallback((xp) => {
    setCurrentQuestionXP(xp);
    setQuestXP(prev => prev + xp);
    setTotalXP(prev => prev + xp);
    setShowXPPopup(true);
  }, []);

  const resetQuestXP = useCallback(() => {
    setQuestXP(0);
  }, []);

  return { 
    totalXP, 
    questXP, 
    showXPPopup, 
    currentQuestionXP, 
    addXP, 
    resetQuestXP,
    setShowXPPopup 
  };
};

// Custom hook for quest progress
const useQuestProgress = (quests, currentQuestIndex, currentQuestionIndex) => {
  const currentQuest = useMemo(() => 
    quests[currentQuestIndex], [quests, currentQuestIndex]
  );

  const currentQuestion = useMemo(() => 
    currentQuest?.questions?.[currentQuestionIndex], 
    [currentQuest, currentQuestionIndex]
  );

  const isLastQuestionInQuest = useMemo(() => 
    currentQuestionIndex === (currentQuest?.questions?.length || 0) - 1,
    [currentQuestionIndex, currentQuest]
  );

  const isLastQuest = useMemo(() => 
    currentQuestIndex === quests.length - 1,
    [currentQuestIndex, quests.length]
  );

  return {
    currentQuest,
    currentQuestion,
    isLastQuestionInQuest,
    isLastQuest
  };
};

// Input Components (same as before)
const TextInput = React.memo(({ value, onChange, theme, placeholder, type = 'text', rows = 1 }) => {
  const commonProps = {
    value: value || '',
    onChange: (e) => onChange(e.target.value),
    placeholder,
    className: "w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-base sm:text-lg transition-all",
    style: {
      borderColor: theme.border,
      backgroundColor: theme.inputBg,
      color: theme.text
    },
    onFocus: (e) => e.target.style.borderColor = theme.primary,
    onBlur: (e) => e.target.style.borderColor = theme.border
  };

  if (rows > 1) {
    return <textarea {...commonProps} rows={rows} />;
  }

  return <input type={type} {...commonProps} />;
});

const MultipleChoiceInput = React.memo(({ options, value, onChange, theme }) => (
  <div className="space-y-3">
    {options?.map((option, idx) => (
      <button
        key={`${option}-${idx}`}
        onClick={() => onChange(option)}
        className="w-full text-left px-4 py-3 border-2 rounded-lg transition-all font-medium text-base sm:text-lg"
        style={{
          borderColor: value === option ? theme.primary : theme.border,
          backgroundColor: value === option ? `${theme.primary}15` : theme.cardBg,
          color: value === option ? theme.primary : theme.text
        }}
      >
        {option}
      </button>
    ))}
  </div>
));

const CheckboxInput = React.memo(({ options, value, onChange, theme }) => {
  const checkboxAnswers = value || [];
  
  return (
    <div className="space-y-3">
      {options?.map((option, idx) => (
        <label 
          key={idx} 
          className="flex items-center gap-3 p-3 border-2 rounded-lg hover:opacity-80 transition-all cursor-pointer"
          style={{
            borderColor: theme.border,
            backgroundColor: theme.cardBg
          }}
        >
          <input
            type="checkbox"
            checked={checkboxAnswers.includes(option)}
            onChange={(e) => {
              const newAnswers = e.target.checked
                ? [...checkboxAnswers, option]
                : checkboxAnswers.filter(item => item !== option);
              onChange(newAnswers);
            }}
            className="w-5 h-5 rounded"
            style={{ accentColor: theme.primary }}
          />
          <span className="font-medium text-base sm:text-lg" style={{ color: theme.text }}>
            {option}
          </span>
        </label>
      ))}
    </div>
  );
});

const RatingInput = React.memo(({ value, onChange, theme }) => (
  <div className="flex gap-2 justify-center">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        onClick={() => onChange(star)}
        className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl sm:text-2xl border-2 rounded-lg transition-all hover:scale-110"
        style={{
          borderColor: value && star <= value ? '#FBBF24' : theme.border,
          backgroundColor: value && star <= value ? '#FEF3C7' : theme.inputBg,
          color: value && star <= value ? '#F59E0B' : theme.textMuted
        }}
      >
        <Star 
          size={20} 
          fill={value && star <= value ? "#F59E0B" : "none"} 
        />
      </button>
    ))}
  </div>
));

const ImageInput = React.memo(({ options, value, onChange, theme }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
    {options?.map((option, idx) => (
      <button
        key={idx}
        onClick={() => onChange(option)}
        className="aspect-square border-2 rounded-lg overflow-hidden transition-all hover:scale-105"
        style={{
          borderColor: value === option ? theme.primary : theme.border,
          boxShadow: value === option ? `0 0 0 2px ${theme.primary}40` : 'none'
        }}
      >
        <img
          src={option}
          alt={`Option ${idx + 1}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = `https://via.placeholder.com/150/cccccc/666666?text=Image+${idx + 1}`;
          }}
        />
      </button>
    ))}
  </div>
));

// Animation variants
const contentVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  },
  exit: { opacity: 0, x: -20 }
};

const slideUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

const PreviewPage = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  
  const [currentState, setCurrentState] = useState('WELCOME');
  const [currentQuestIndex, setCurrentQuestIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completedQuests, setCompletedQuests] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [quests, setQuests] = useState([]);

  // Custom hooks
  const { 
    totalXP, 
    questXP, 
    showXPPopup, 
    currentQuestionXP, 
    addXP, 
    resetQuestXP,
    setShowXPPopup 
  } = useXPManagement();

  const {
    currentQuest,
    currentQuestion,
    isLastQuestionInQuest,
    isLastQuest
  } = useQuestProgress(quests, currentQuestIndex, currentQuestionIndex);

  // Get theme colors
  const theme = useMemo(() => 
    getThemeColors(form?.settings?.theme || 'white'), 
    [form?.settings?.theme]
  );

  // Load form data
  useEffect(() => {
    const loadFormData = async () => {
      try {
        setIsLoading(true);
        // TODO: Replace with actual API call to fetch form by ID
        // const formData = await formService.getFormById(formId);
        // const questsData = await formService.getFormQuests(formId);
        
        // Mock data for demonstration
        const mockForm = {
          id: formId,
          title: "Sample Adventure Form",
          settings: {
            theme: 'blue'
          }
        };
        
        const mockQuests = [
          {
            id: 1,
            title: "Welcome Quest",
            questions: [
              {
                id: 1,
                text: "What's your name?",
                type: 'text',
                required: true,
                points: 10
              },
              {
                id: 2,
                text: "How would you rate your experience?",
                type: 'rating',
                required: false,
                points: 5
              }
            ]
          },
          {
            id: 2,
            title: "Main Quest",
            questions: [
              {
                id: 3,
                text: "Choose your favorite option:",
                type: 'multiple-choice',
                options: ['Option A', 'Option B', 'Option C'],
                required: true,
                points: 15
              }
            ]
          }
        ];

        setForm(mockForm);
        setQuests(mockQuests);
        
        // Reset state
        setCurrentState('WELCOME');
        setCurrentQuestIndex(0);
        setCurrentQuestionIndex(0);
        setCompletedQuests([]);
        setAnswers({});
        resetQuestXP();
        setShowXPPopup(false);
      } catch (error) {
        console.error('Error loading form:', error);
        navigate('/dashboard'); // Redirect to dashboard on error
      } finally {
        setIsLoading(false);
      }
    };

    if (formId) {
      loadFormData();
    }
  }, [formId, navigate, resetQuestXP, setShowXPPopup]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        handleGoHome();
      }
      
      if (currentState === 'QUEST_QUESTIONS') {
        if (e.key === 'ArrowLeft') {
          handlePreviousQuestion();
        }
        if (e.key === 'ArrowRight' && !isNextButtonDisabled()) {
          handleNextQuestion();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentState]);

  // Auto-hide XP popup
  useEffect(() => {
    let timeoutId;
    
    if (showXPPopup) {
      timeoutId = setTimeout(() => {
        setShowXPPopup(false);
      }, 3000);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showXPPopup]);

  // Answer validation
  const validateAnswer = useCallback((question, answer) => {
    if (!question?.required) return true;
    
    switch (question.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'phone':
      case 'date':
        return answer && answer.toString().trim().length > 0;
      
      case 'multiple-choice':
        return answer !== undefined && answer !== null;
      
      case 'checkbox':
        return Array.isArray(answer) && answer.length > 0;
      
      case 'rating':
        return answer !== undefined && answer > 0;
      
      case 'image':
        return answer !== undefined;
      
      default:
        return true;
    }
  }, []);

  const areAllRequiredAnswered = useCallback(() => {
    return currentQuest?.questions?.every(question => 
      !question.required || validateAnswer(question, answers[question.id])
    ) ?? false;
  }, [currentQuest, answers, validateAnswer]);

  const isNextButtonDisabled = useCallback(() => {
    return currentQuestion?.required && !validateAnswer(currentQuestion, answers[currentQuestion?.id]);
  }, [currentQuestion, answers, validateAnswer]);

  // Get current answer
  const getCurrentAnswer = useCallback(() => {
    return answers[currentQuestion?.id];
  }, [answers, currentQuestion]);

  // Navigation handlers
  const handleGoHome = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleStartAdventure = useCallback(() => {
    setCurrentState('QUEST_MAP');
  }, []);

  const handleQuestClick = useCallback(async (questIndex) => {
    try {
      setIsLoading(true);
      // Only allow starting unlocked quests that aren't completed
      if (questIndex === 0 || completedQuests.includes(questIndex - 1)) {
        if (!completedQuests.includes(questIndex)) {
          setCurrentQuestIndex(questIndex);
          setCurrentQuestionIndex(0);
          resetQuestXP();
          setCurrentState('QUEST_QUESTIONS');
        }
      }
    } catch (error) {
      console.error('Error starting quest:', error);
    } finally {
      setIsLoading(false);
    }
  }, [completedQuests, resetQuestXP]);

  const handleAnswerChange = useCallback((value) => {
    if (!currentQuestion) return;

    const previousAnswer = answers[currentQuestion.id];
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: value
    };
    setAnswers(newAnswers);

    // Show XP popup if this question has points and we're answering it for the first time
    if (currentQuestion.points > 0 && !previousAnswer) {
      addXP(currentQuestion.points);
    }
  }, [currentQuestion, answers, addXP]);

  const handleNextQuestion = useCallback(() => {
    if (isLastQuestionInQuest) {
      // Check if all required questions are answered
      if (!areAllRequiredAnswered()) {
        alert('Please answer all required questions before completing this quest.');
        return;
      }

      // Complete current quest
      const newCompletedQuests = [...completedQuests, currentQuestIndex];
      setCompletedQuests(newCompletedQuests);
      
      // If it's the last quest, go to form complete
      if (isLastQuest) {
        setCurrentState('FORM_COMPLETE');
      } else {
        setCurrentState('QUEST_COMPLETE');
      }
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowXPPopup(false);
    }
  }, [
    isLastQuestionInQuest, 
    areAllRequiredAnswered, 
    completedQuests, 
    currentQuestIndex, 
    isLastQuest, 
    setShowXPPopup
  ]);

  const handlePreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowXPPopup(false);
    }
  }, [currentQuestionIndex, setShowXPPopup]);

  const handleBackToMap = useCallback(() => {
    setCurrentState('QUEST_MAP');
    setShowXPPopup(false);
  }, [setShowXPPopup]);

  const handleNextQuest = useCallback(() => {
    setCurrentState('QUEST_MAP');
  }, []);

  const handleCompleteForm = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // Calculate XP totals
  const getCurrentQuestTotalXP = useCallback(() => {
    return currentQuest?.questions?.reduce((total, question) => 
      total + (question.points || 0), 0
    ) || 0;
  }, [currentQuest]);

  const getCurrentQuestEarnedXP = useCallback(() => {
    return currentQuest?.questions?.reduce((total, question) => {
      if (answers[question.id]) {
        return total + (question.points || 0);
      }
      return total;
    }, 0) || 0;
  }, [currentQuest, answers]);

  const getTotalPossibleFormXP = useCallback(() => {
    return quests.reduce((total, quest) => 
      total + (quest.questions?.reduce((questTotal, question) => 
        questTotal + (question.points || 0), 0) || 0), 0
    );
  }, [quests]);

  // Render question input based on type
  const renderQuestionInput = useCallback((question) => {
    if (!question) return null;

    const currentAnswer = getCurrentAnswer();

    switch (question.type) {
      case 'text':
        return (
          <TextInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="Type your answer here..."
            rows={4}
          />
        );
      
      case 'multiple-choice':
        return (
          <MultipleChoiceInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );
      
      case 'checkbox':
        return (
          <CheckboxInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );
      
      case 'rating':
        return (
          <RatingInput
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );
      
      case 'email':
        return (
          <TextInput
            type="email"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="your@email.com"
          />
        );
      
      case 'number':
        return (
          <TextInput
            type="number"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="Enter a number"
          />
        );
      
      case 'phone':
        return (
          <TextInput
            type="tel"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
            placeholder="+1 (555) 123-4567"
          />
        );
      
      case 'date':
        return (
          <TextInput
            type="date"
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );
      
      case 'image':
        return (
          <ImageInput
            options={question.options}
            value={currentAnswer}
            onChange={handleAnswerChange}
            theme={theme}
          />
        );
      
      default:
        return (
          <div className="text-gray-500 italic text-base sm:text-lg">
            Unsupported question type: {question.type}
          </div>
        );
    }
  }, [getCurrentAnswer, handleAnswerChange, theme]);

  // Render different states
  const renderContent = useCallback(() => {
    if (!form || quests.length === 0) return null;

    switch (currentState) {
      case 'WELCOME':
        return (
          <motion.div
            key="welcome"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center space-y-6 sm:space-y-8 px-4 sm:px-0"
          >
            <h1 className="text-2xl sm:text-4xl font-bold leading-tight" style={{ color: theme.text }}>
              Welcome to {form.title}!
            </h1>
            
            <div className="scale-90 sm:scale-100">
              <QuestIllustration 
                quests={quests}
                currentQuestIndex={-1}
                completedQuests={[]}
                isInteractive={false}
              />
            </div>
            
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: theme.textSecondary }}>
              Complete quests and earn XP! Your adventure starts now.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStartAdventure}
              className="px-6 py-3 sm:px-8 sm:py-4 text-white rounded-xl font-bold text-base sm:text-lg flex items-center gap-2 mx-auto"
              style={{ backgroundColor: theme.primary }}
              disabled={isLoading}
            >
              <Play size={20} />
              {isLoading ? 'Loading...' : 'Start Adventure'}
            </motion.button>
          </motion.div>
        );

      case 'QUEST_MAP':
        return (
          <motion.div
            key="quest-map"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-6 px-4 sm:px-0"
          >
            <div className="text-center space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: theme.text }}>
                Quest Map
              </h2>
              <p className="text-base sm:text-lg" style={{ color: theme.textSecondary }}>
                Choose your next adventure
              </p>
            </div>

            <QuestIllustration 
              quests={quests}
              currentQuestIndex={currentQuestIndex}
              completedQuests={completedQuests}
              onQuestClick={handleQuestClick}
              isInteractive={true}
            />

            {/* Progress Summary */}
            <motion.div 
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              className="text-center p-4 rounded-xl border"
              style={{
                backgroundColor: theme.cardBg,
                borderColor: theme.border
              }}
            >
              <div className="text-sm" style={{ color: theme.textSecondary }}>
                Progress: {completedQuests.length} / {quests.length} Quests Completed
              </div>
              <div className="text-2xl font-bold mt-1" style={{ color: theme.primary }}>
                {totalXP} XP Earned
              </div>
            </motion.div>
          </motion.div>
        );

      case 'QUEST_QUESTIONS':
        if (!currentQuest || !currentQuestion) {
          return (
            <div className="text-center text-red-500">
              Error: Quest or question not found
            </div>
          );
        }

        const progressPercentage = ((currentQuestionIndex + 1) / currentQuest.questions.length) * 100;

        return (
          <motion.div
            key="quest-questions"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="max-w-2xl mx-auto space-y-4 sm:space-y-6 px-4 sm:px-0"
          >
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: theme.textSecondary }}>
                  Question {currentQuestionIndex + 1} of {currentQuest.questions.length}
                </span>
                <span className="font-semibold" style={{ color: theme.primary }}>
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: theme.border }}>
                <motion.div 
                  className="h-full"
                  style={{ backgroundColor: theme.primary }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            {/* Question Card */}
            <motion.div 
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              className="rounded-xl sm:rounded-2xl border p-4 sm:p-6 lg:p-8 shadow-sm relative"
              style={{
                backgroundColor: theme.cardBg,
                borderColor: theme.border
              }}
            >
              {/* XP Popup */}
              <XPPopup 
                xp={currentQuestionXP} 
                isVisible={showXPPopup}
              />
              
              {/* Question Header */}
              <div className="flex items-start justify-between mb-4 sm:mb-6">
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold mb-2 leading-tight" style={{ color: theme.text }}>
                    {currentQuestion.text}
                  </h3>
                  {currentQuestion.required && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded-full text-xs font-medium">
                      <span>Required</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Question Input Area */}
              <div className="space-y-4">
                {renderQuestionInput(currentQuestion)}
              </div>

              {/* Pro Tip */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg border"
                style={{
                  backgroundColor: `${theme.primary}10`,
                  borderColor: theme.border
                }}
              >
                <div className="flex items-center gap-2 text-sm sm:text-base" style={{ color: theme.primary }}>
                  <span className="font-semibold">Pro tip:</span>
                  <span>
                    {currentQuestion.points > 0 ? `Earn ${currentQuestion.points} XP for answering!` :
                     currentQuestion.type === 'multiple-choice' ? 'Choose the best option!' :
                     currentQuestion.type === 'checkbox' ? 'Select all that apply!' :
                     currentQuestion.type === 'rating' ? 'Rate your experience!' :
                     'Complete this question to progress!'}
                  </span>
                </div>
              </motion.div>
            </motion.div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-4">
              <button
                onClick={currentQuestionIndex === 0 ? handleBackToMap : handlePreviousQuestion}
                className="px-6 py-3 rounded-lg bg-gray-600 text-white font-medium flex items-center gap-1 sm:gap-2 text-sm sm:text-base hover:bg-gray-700 transition-colors"
                disabled={isLoading}
              >
                <ChevronLeft size={18} />
                <span className="hidden sm:inline">
                  {currentQuestionIndex === 0 ? 'Back to Map' : 'Previous'}
                </span>
              </button>
              
              <button
                onClick={handleNextQuestion}
                disabled={isNextButtonDisabled() || isLoading}
                className="px-4 py-2 sm:px-6 sm:py-3 text-white bg-blue-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1 sm:gap-2 text-sm sm:text-base hover:bg-blue-700 transition-colors"
              >
                <span>{isLastQuestionInQuest ? 'Finish Quest' : 'Next'}</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        );

      case 'QUEST_COMPLETE':
        const earnedXP = getCurrentQuestEarnedXP();
        const totalPossibleXP = getCurrentQuestTotalXP();
        
        return (
          <motion.div
            key="quest-complete"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center space-y-8 px-4 sm:px-0"
          >
            {/* Quest Complete Card */}
            <motion.div
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-300 max-w-md mx-auto shadow-lg"
            >
              <div className="space-y-6">
                <div>
                  <motion.h2 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2"
                  >
                    Quest Complete! 🎉
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-gray-600 text-lg"
                  >
                    {currentQuest?.title}
                  </motion.p>
                </div>

                {/* XP Breakdown */}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{earnedXP}</div>
                    <div className="text-sm text-blue-800 font-medium">XP Earned</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{totalPossibleXP}</div>
                    <div className="text-sm text-blue-600 font-medium">Total XP</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Next Quest Button */}
            <motion.button
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNextQuest}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-lg flex items-center gap-3 mx-auto shadow-lg hover:shadow-xl transition-all"
              disabled={isLoading}
            >
              <span>{isLastQuest ? 'View Results' : 'Continue to Quest Map'}</span>
              <ChevronRight size={20} />
            </motion.button>
          </motion.div>
        );

      case 'FORM_COMPLETE':
        const totalPossibleFormXP = getTotalPossibleFormXP();
        const formCompletionPercentage = totalPossibleFormXP > 0 ? (totalXP / totalPossibleFormXP) * 100 : 100;

        return (
          <motion.div
            key="form-complete"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center space-y-8 px-4 sm:px-0"
          >
            <motion.div
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 border border-gray-200 max-w-2xl mx-auto shadow-xl"
            >
              <div className="space-y-8">
                <div className="space-y-3">
                  <motion.h1
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 bg-clip-text text-transparent"
                  >
                    Amazing Achievement! 🎉
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-xl text-gray-600"
                  >
                    You've completed {form.title}
                  </motion.p>
                </div>

                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg"
                >
                  <div className="space-y-4">
                    <div className="text-2xl sm:text-3xl font-bold">Total XP Earned</div>
                    <div className="text-4xl sm:text-6xl font-black text-yellow-300">
                      {totalXP} <span className="text-xl sm:text-2xl text-white">XP</span>
                    </div>
                    <div className="text-sm opacity-90">
                      {Math.round(formCompletionPercentage)}% of possible XP
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">{quests.length}</div>
                    <div className="text-sm text-gray-600 font-medium">Quests Completed</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-green-600">{totalPossibleFormXP}</div>
                    <div className="text-sm text-gray-600 font-medium">Total Possible XP</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.button
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCompleteForm}
              className="px-8 py-4 text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all"
              style={{
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryHover || theme.primary})`
              }}
            >
              Return to Dashboard
            </motion.button>
          </motion.div>
        );

      default:
        return (
          <div className="text-center text-red-500">
            Unknown state: {currentState}
          </div>
        );
    }
  }, [
    form,
    quests,
    currentState,
    theme,
    handleStartAdventure,
    isLoading,
    handleQuestClick,
    currentQuestIndex,
    completedQuests,
    totalXP,
    currentQuest,
    currentQuestion,
    currentQuestionIndex,
    showXPPopup,
    currentQuestionXP,
    renderQuestionInput,
    handleBackToMap,
    handlePreviousQuestion,
    isNextButtonDisabled,
    handleNextQuestion,
    isLastQuestionInQuest,
    getCurrentQuestEarnedXP,
    getCurrentQuestTotalXP,
    handleNextQuest,
    isLastQuest,
    getTotalPossibleFormXP,
    handleCompleteForm
  ]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!form || quests.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Form Not Found</h1>
          <button
            onClick={handleGoHome}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen w-full overflow-auto"
      style={{ backgroundColor: theme.background }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 sm:p-6 border-b sticky top-0 z-10"
        style={{ 
          borderColor: theme.border,
          backgroundColor: theme.background 
        }}
      >
        <button
          onClick={handleGoHome}
          className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: theme.text }}
        >
          <Home size={20} />
          <span className="hidden sm:inline">Home</span>
        </button>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <div 
            className="w-2 h-2 sm:w-3 sm:h-3 rounded-full"
            style={{
              backgroundColor: currentState === 'WELCOME' ? theme.textMuted :
                             currentState === 'QUEST_MAP' ? theme.primary :
                             currentState === 'QUEST_QUESTIONS' ? theme.primary :
                             currentState === 'QUEST_COMPLETE' ? '#10B981' :
                             currentState === 'FORM_COMPLETE' ? '#10B981' : theme.textMuted
            }}
          />
          <span className="text-xs sm:text-sm font-medium capitalize" style={{ color: theme.textSecondary }}>
            {currentState === 'QUEST_QUESTIONS' ? `${currentQuest?.title}` : 
              currentState === 'QUEST_COMPLETE' ? 'Quest Complete' :
              currentState === 'QUEST_MAP' ? 'Quest Map' :
              currentState === 'FORM_COMPLETE' ? 'All Quests Complete' :
              currentState.toLowerCase().replace('_', ' ')}
          </span>
        </div>
        
        {/* Total XP Badge */}
        {(currentState === 'QUEST_QUESTIONS' || currentState === 'QUEST_COMPLETE' || currentState === 'QUEST_MAP') && totalXP > 0 && (
          <div 
            className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium"
            style={{
              backgroundColor: theme.xpBg || '#FEF3C7',
              color: theme.xpText || '#F59E0B'
            }}
          >
            <span>⭐ {totalXP} XP</span>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 py-8 sm:py-12">
        <div className="container mx-auto max-w-6xl">
          <AnimatePresence mode="wait">
            {renderContent()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default PreviewPage;